import { useMemo } from 'react';

import { isAgentProxyMode } from '@/utils/agent-proxy';

import { useAppConfig } from './use-app-config';

/**
 * Hook that provides centralized branch configuration state
 * Used by components that need to display branch/environment indicators
 */
export const useBranchConfig = () => {
  const { data: envConfig, isLoading } = useAppConfig();

  return useMemo(() => {
    if (!envConfig) {
      return {
        isLoading: true,
        isLocal: false,
        uiBranch: 'main',
        deployedBranch: 'main',
        isProduction: false,
        isPreview: false,
        showBanner: false,
        showBorder: false,
        environment: undefined,
      };
    }

    // Calculate branch states
    // NB: Backend _always_ serves main UI regardless of deployed branch, except when preview cookie is present.
    // Hence our ability to predict `main` if no preview cookie is present.
    const isLocal = import.meta.env.DEV;
    const bffBranch = isLocal ? '∅' : envConfig.build.branch;
    const uiBranch = isLocal ? '∅' : envConfig.build.previewBranch || 'main';

    const isProduction = envConfig.deployment.isProduction;
    const isPreview = !!envConfig.build.previewBranch;
    const isAgentProxy = isAgentProxyMode(envConfig);

    // Display policy is based on config facts.
    // A. Show branch banner if previewing in any env so that user has a nice "clear preview" button
    //    In AgentProxy mode, hide the banner (matches Darkwing behavior - agent mode is considered "normal")
    const showBanner = (isLocal || isPreview) && !isAgentProxy;
    // B. Show environment border outside of production (or when banner is shown)
    //    In AgentProxy mode, hide the border as well
    const showBorder = (!isProduction || showBanner) && !isAgentProxy;

    return {
      isLoading,
      isLocal,
      uiBranch,
      deployedBranch: bffBranch,
      isProduction,
      isPreview,
      showBanner,
      showBorder,
      environment: envConfig.deployment.environment,
    };
  }, [envConfig, isLoading]);
};
